// Libraries
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { isNil, isNotNil, mergeRight } from 'ramda';
import moment from 'moment';

// MUI
import { Table, TableBody, TableCell, TableRow } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Grid from '@mui/material/Grid';

// Core
import { ParameterValues, paramRepository, ResourceTextApplication } from '../../../core/resources';
import FormValidator from '../../../core/FormValidator';
import Utils from '../../../core/utils';
import { dayFormat } from '../../../core/constants';
import utils from '../../../core/utils';
import authorization from '../../../core/authorization';

// Common
import {
    ADialog,
    ADialogActions,
    ADialogButtonsProp,
    ADialogContent,
    ADialogTitle,
    ConfirmDeletionDialog,
} from '../../../common/dialog';
import { ADataGrid, AGridColumns } from '../../../common/dataGrid';
import { AAutocompleteValue, AInputItem, ARadioButtons } from '../../../common/inputs';
import Annotation from '../../../common/annotations/Annotation';
import { AAutocomplete } from '../../../common/inputs';
import { AAddButton, ADeleteIconButton, AEditIconButton } from '../../../common/buttons';
import { TextWithTooltip } from '../../../common/typography';

// Interfaces
import {
    IAnimalSummary,
    IAnimalSummaryExtraInfo,
    IFilteredAnimalList,
} from '../../../interfaces/IAnimal';
import { EmptyGUID, GUIDType } from '../../../interfaces/types';
import { IMainGroup, ISubGroup } from '../../../interfaces/IGroup';
import { FarmSettingTypeEnum, IFarmSettings } from '../../../interfaces/IFarmSettings';

// Store
// API
import groupsApi from '../../../api/groupsApi';

// Feature
import AddAnimalDialog from '../../shared/components/AddAnimalDialog';
import { SecondaryDialogTypeEnum } from '../../shared/types/enum';
import AddGroupDialog from './AddGroupDialog';
import '../../shared/styles/sharedStyles.scss';
import farmSettingsApi from '../../../api/farm/farmSettingsApi';
import CreatePreliminaryAnnouncementsForGroupSwitch from './CreatePreliminaryAnnouncementsForGroupSwitch';
import CreatePreliminaryAnnouncementDialog from './CreatePreliminaryAnnouncementDialog';
import companyApi from '../../../api/companyApi';
import { IFarmDetails } from '../../../interfaces/IBusinessEntities';
import animalAnnouncementApi from '../../../api/animalAnnouncementApi';
import {
    ICollectWeek,
    ICollectWeekData,
    ICollectWeekResult,
    ICreateAnnouncement,
    IUpdateAnnouncementResult,
} from '../../../interfaces/IAnnouncement';
import {
    mapAnimalSummaryToIAnimalAnnouncementAnimal,
    mapBovineInfos,
} from '../../announcements/helpers/data';
import CreatePreliminaryPurchaseAnnouncementDialog from './CreatePreliminaryPurchaseAnnouncementDialog';

import { AliasTypeEnum, AnnouncementTypeEnum, SalesType } from '../../../interfaces/enums';
import CreatedPreliminaryAnnouncementsDialog from './CreatedPreliminaryAnnouncementsDialog';
import { defaultSalesType } from '../../announcements/helpers/salesType';

export interface IGroupDialogProps {
    onClose: () => void;
    onDataChange: (maingroups: IMainGroup[]) => void;
    allAnimals: IFilteredAnimalList[] | null;
    animals: IAnimalSummaryExtraInfo[];
    selectedAnimals: IAnimalSummary[];
    farmGuid: string;
    addNewAnnouncementToList: (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string) => void;
    /** If the GroupDialog was opened from 'animal waiting room' or not */
    waitingRoom: boolean;
}

export interface IToggleTracker {
    id: GUIDType;
    toggleStatus: boolean;
}

export interface IAnimalDeletionModel {
    animalId: GUIDType;
    groupId: GUIDType;
}

export default function GroupDialog(props: IGroupDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaBovine', 'AnelmaGeneral', 'AnelmaAnimalAnnouncement']);
    const { enqueueSnackbar } = useSnackbar();
    const auth = authorization;
    const COL_W_S: number = 100;
    const COL_W_M: number = 150;
    const validator = new FormValidator();
    const [paramBovineSex, setParamBovineSex] = useState<ParameterValues | null>(null);
    const [farmDetails, setFarmDetails] = useState<IFarmDetails>();
    const [selectedAnimals, setSelectedAnimals] = useState<IAnimalSummary[]>(props.selectedAnimals);
    const [selectedDialogOption, setSelectedDialogOption] = useState<number>(1);
    const [addAnimalDialogVisible, setAddAnimalDialogVisible] = useState<boolean>(false);
    const [addGroupDialogVisible, setAddGroupDialogVisible] = useState<boolean>(false);
    const [preliminaryAnnouncementData, setPreliminaryAnnouncementData] =
        useState<ICreateAnnouncement>();
    const [showCreateAnnouncementDialog, setShowCreateAnnouncementDialog] =
        useState<boolean>(false);
    const [showCreatePurchaseDialog, setShowCreatePurchaseDialog] = useState<boolean>(false);
    const [showCreatedPreliminaryAnnouncementsDialog, setShowCreatedPreliminaryAnnouncementsDialog] =
        useState<boolean>(false);
    const [animals, setAnimals] = useState<IAnimalSummaryExtraInfo[]>([]);
    const [mainGroups, setMainGroups] = useState<IMainGroup[]>([]);
    const [toggleTracker, setToggleTracker] = useState<IToggleTracker[]>([]);
    const [addDialogMode, setAddDialogMode] = useState<string>('add');
    const [mainGroupSelections, setMainGroupSelections] = useState<
        AInputItem<AAutocompleteValue>[]
    >([]);
    const [subGroupSelections, setSubGroupSelections] = useState<AInputItem<AAutocompleteValue>[]>(
        []
    );
    const [subGroupTargetSelections, setSubGroupTargetSelections] = useState<
        AInputItem<AAutocompleteValue>[]
    >([]);
    const [addDialogData, setAddDialogData] = useState<IMainGroup>({
        Name: '',
        Id: '',
        ChildGroups: [],
    });
    const [selectedMainGroup, setSelectedMainGroup] = useState<AAutocompleteValue>('');
    const [selectedSubGroup, setSelectedSubGroup] = useState<AAutocompleteValue>('');
    const [mainGroupTarget, setMainGroupTarget] = useState<AAutocompleteValue>('');
    const [subGroupTarget, setSubGroupTarget] = useState<AAutocompleteValue>('');
    const [showConfirmDeleteMain, setShowConfirmDeleteMain] = useState<boolean>(false);
    const [showConfirmDeleteSub, setShowConfirmDeleteSub] = useState<boolean>(false);
    const [showConfirmDeleteAnimal, setShowConfirmDeleteAnimal] = useState<boolean>(false);
    const [animalsToDelete, setAnimalsToDelete] = useState<IAnimalDeletionModel[]>([]);
    const [animalToDelete, setAnimalToDelete] = useState<IAnimalDeletionModel|null>(null);
    const [dataGridAnimals, setDataGridAnimals] = useState<GridRowSelectionModel>(
        props.selectedAnimals.map((r) => r.Id)
    );

    const [animalsToSave, setAnimalsToSave] = useState<string[]>([]);
    const [subGroupToDelete, setSubGroupToDelete] = useState<ISubGroup>();
    const [mainGroupToDelete, setMainGroupToDelete] = useState<IMainGroup>();
    const [showAnnotation, setShowAnnotation] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>();
    const [farmSettings, setFarmSettings] = useState<IFarmSettings>();
    const [createPreliminaryAnnouncements, setCreatePreliminaryAnnouncements] =
        useState<boolean>(false);

    const [producerNumber, setProducerNumber] = useState<number>(0);
    const [collectWeekStart, setCollectWeekStart] = useState<ICollectWeek>();
    const [collectWeekEnd, setCollectWeekEnd] = useState<ICollectWeek>();
    const [announcementId, setAnnouncementId] = useState<GUIDType>();
    const [announcementType, setAnnouncementType] = useState<AnnouncementTypeEnum>();

    useEffect(() => {
        if (props.selectedAnimals.length > 0) setSelectedDialogOption(2);
        if (props.farmGuid && props.farmGuid.length > 0) {
            getGroups(props.farmGuid);
            getPreliminaryAnnouncementValue();
        }

        setParamBovineSex(paramRepository.resource('AnelmaBovine', 'BovineSex'));
    }, []);

    useEffect(() => {
        var trackerStatusArray = [];

        for (let i = 0; i < mainGroups.length; i++) {
            var trackerStatusItem: IToggleTracker = {
                id: mainGroups[i].Id,
                toggleStatus: false,
            };
            trackerStatusArray.push(trackerStatusItem);
        }
        setToggleTracker(trackerStatusArray);
    }, [mainGroups]);

    useEffect(() => {
        if (props.allAnimals && props.allAnimals.length > 0) {
            let allAnimalsArray = props.allAnimals.find((a) => a.FilterType === 99);
            if (allAnimalsArray !== undefined && allAnimalsArray.Animals.length > 0) {
                setAnimals([...allAnimalsArray.Animals] as IAnimalSummaryExtraInfo[]);
            }
        }
    }, [props.allAnimals]);

    useEffect(() => {
        if (props.farmGuid) {
            companyApi.getCompanyByGuid(props.farmGuid).then((response) => {
                setFarmDetails(response?.Entity as IFarmDetails);

                const producerNumberContext = response?.Entity.Aliases.find(
                    (i) => i.Type === AliasTypeEnum.ProducerNumber
                );
                setProducerNumber(producerNumberContext?.Value as unknown as number);
            });
        }
    }, [props.farmGuid]);

    useEffect(() => {
        let animalsToDelete: IAnimalDeletionModel[] = [];
        let childGroupId = '';
        let lastChildGroupId = '';

        for (let i = 0; i < props.selectedAnimals.length; i++) {
            let cg = mainGroups.find((m) =>
                m.ChildGroups.find((c) =>
                    c.Animals.find((a) => a.AnimalGUID === props.selectedAnimals[i].Id)
                )
            )?.ChildGroups;

            childGroupId =
                cg?.find(
                    (c) =>
                        c.Animals.find((ca) => ca.AnimalGUID === props.selectedAnimals[i].Id)
                            ?.AnimalGUID
                )?.Id || '';

            if (areGroupIdsInvalid(lastChildGroupId, childGroupId)) setShowAnnotation(true);

            lastChildGroupId = childGroupId;

            animalsToDelete.push({ animalId: props.selectedAnimals[i].Id, groupId: childGroupId });
        }

        if (animalsToSave.length === 0) setAnimalsToSave(props.selectedAnimals.map((_) => _.Id));

        setAnimalsToDelete(animalsToDelete);
    }, [props.selectedAnimals, mainGroups]);

    const dialogOptions: AInputItem<number>[] = [
        { text: t('AnelmaBovine:1101'), value: 1 },
        { text: t('AnelmaBovine:1100'), value: 2 },
    ];

    useEffect(() => {
        var subGroupSelections: AInputItem<AAutocompleteValue>[] = [];
        var mainGroup = mainGroups.find((i) => i.Id === selectedMainGroup) || {
            Name: '',
            Id: '',
            ChildGroups: [],
        };
        if (mainGroup !== null && mainGroup.Id !== '00000000-0000-0000-0000-000000000000') {
            for (let i = 0; i < mainGroup.ChildGroups.length; i++) {
                subGroupSelections.push({
                    text: mainGroup.ChildGroups[i].Name,
                    value: mainGroup.ChildGroups[i].Id,
                });
            }
            setSubGroupSelections(subGroupSelections);
        }
    }, [selectedMainGroup]);

    useEffect(() => {
        var subGroupTargetSelections: AInputItem<AAutocompleteValue>[] = [];
        setSubGroupTargetSelections(subGroupTargetSelections);
        var mainGroup = mainGroups.find((i) => i.Id === mainGroupTarget) || {
            Name: '',
            Id: '',
            ChildGroups: [],
        };

        if (mainGroup !== null && mainGroup.Id !== '00000000-0000-0000-0000-000000000000') {
            for (let i = 0; i < mainGroup.ChildGroups.length; i++) {
                subGroupTargetSelections.push({
                    text: mainGroup.ChildGroups[i].Name,
                    value: mainGroup.ChildGroups[i].Id,
                });
            }
            setSubGroupTargetSelections(subGroupTargetSelections);
        }
    }, [mainGroupTarget]);

    useEffect(() => {
        let newValues = [...props.selectedAnimals];
        let maingrp = mainGroups.find((i) => i.Id === selectedMainGroup);
        if (maingrp !== undefined && maingrp?.ChildGroups.length > 0) {
            if (selectedSubGroup) {
                let subGrp: ISubGroup = maingrp.ChildGroups.find(
                    (c) => c.Id === selectedSubGroup
                ) || {
                    Animals: [],
                    Id: '',
                    Name: '',
                };
                if (subGrp !== undefined) {
                    for (let ind = 0; ind < subGrp.Animals.length; ind++) {
                        let animal = animals.find(
                            (i) => i.Id === subGrp.Animals[ind].AnimalGUID
                        ) as IAnimalSummary;
                        if (animal) {
                            newValues.push(animal as IAnimalSummary);
                        }
                    }

                    if (subGrp.Animals.length === 0) {
                        newValues = [] as IAnimalSummary[];
                    }
                }
            } else {
                for (let i = 0; i < maingrp?.ChildGroups.length; i++) {
                    var childgrp = maingrp.ChildGroups[i];
                    for (let index = 0; index < childgrp.Animals.length; index++) {
                        let animal = animals.find(
                            (a) => a.Id === childgrp.Animals[index].AnimalGUID
                        ) as IAnimalSummary;
                        if (animal) {
                            newValues.push(animal as IAnimalSummary);
                        }
                    }
                }
            }
        }

        if (selectedMainGroup === '00000000-0000-0000-0000-000000000000') {
            for (let ind = 0; ind < animals.length; ind++) {
                if (
                    !mainGroups.find((m) =>
                        m.ChildGroups.find((c) =>
                            c.Animals.find((a) => a.AnimalGUID === animals[ind].Id)
                        )
                    )
                ) {
                    newValues.push(animals[ind]);
                }
            }
        }

        /*  AN-2413 Filter out duplicates before inserting the 'newValues' to state as above between the lines 234 - 283 duplicates are not filtered out from the mentioned list.
            This is easier than to refactor the lines.
        */
        setSelectedAnimals(newValues.filter((v, i, a) => a.indexOf(v) === i));
    }, [selectedMainGroup, selectedSubGroup]);

    useEffect(() => {
        if (animalToDelete != null)
            setShowConfirmDeleteAnimal(true);
    }, [animalToDelete]);

    const getPreliminaryAnnouncementValue = () => {
        const farmId = props.farmGuid;
        if (isNotNil(farmId)) {
            setIsLoading(true);

            farmSettingsApi.getFarmSettings(farmId).then((response) => {
                if (!response) {
                    enqueueSnackbar(`${t('AnelmaSolmuErrors:1000')}, 'error'`);
                    setIsLoading(false);
                    return;
                }

                if (response.Entity.FarmGuid !== EmptyGUID) {
                    setFarmSettings(response.Entity);
                }
                setIsLoading(false);
            });
        }
    };

    const getGroups = (farmId: string) => {
        setIsLoading(true);

        groupsApi.getGroups(farmId).then((response) => {
            if (!response) {
                enqueueSnackbar(t('AnelmaBovine:1178'), {
                    variant: 'error',
                });
                setIsLoading(false);
                return;
            }
            setMainGroups(response.Items);

            setMainGroupSelections([
                {
                    text: t('AnelmaBovine:1103'),
                    value: '00000000-0000-0000-0000-000000000000',
                },
                ...response.Items.map((item) => ({
                    text: item.Name,
                    value: item.Id,
                })),
            ]);
            setIsLoading(false);
        });
    };

    const getPurchaceCollectWeek = (saleColectWeek: ICollectWeek) => {
        var temp: ICollectWeek = { Week: saleColectWeek.Week, Year: saleColectWeek.Year };

        // handle year change
        if (moment().year(temp.Year).isoWeeksInYear() > temp.Week + 1)
            temp.Week++;
        else {
            temp.Year++;
            temp.Week = 1;
        }

        return temp;
    }

    const columns: AGridColumns = [
        {
            field: 'EuIdentifier',
            headerName: t('AnelmaBovine:1005'),
            renderCell: (params) => {
                const value = (params.row as IAnimalSummary)?.EuIdentifier;

                let formattedEU = Utils.formatEuIdentifier(value);
                if (formattedEU.indexOf('-')) {
                    const splittedValue = formattedEU.split('-');
                    const start = splittedValue[0].length - 4;
                    const end = splittedValue[0].length;

                    const startStr = formattedEU.substring(0, start);
                    const endStr = formattedEU.substring(start, end);

                    return (
                        <>
                            {startStr}
                            <b>{endStr}</b>-{splittedValue[1]}
                        </>
                    );
                }

                return formattedEU;
            },
            type: 'string',
            width: COL_W_M,
        },
        {
            field: 'BirthDate',
            headerName: t('AnelmaBovine:1011'),
            renderCell: (params) => {
                const value = params.value ? moment(String(params.value)).format(dayFormat) : '';

                return <TextWithTooltip text={value} />;
            },
            valueGetter: (params, row) => {
                const birthDate = row.BirthDate;
                return birthDate ? moment(birthDate) : null;
            },
            width: 160,
        },
        {
            field: 'Sex',
            headerName: t('AnelmaBovine:1009'),
            type: 'string',
            width: COL_W_M,
            valueGetter: (params, row) => {
                const value = (row as IAnimalSummaryExtraInfo)?.Sex;

                return paramBovineSex
                    ? (paramBovineSex.find((p) => value === Number(p.code))?.text as string)
                    : '';
            },
        },
        {
            field: 'AgeMonths',
            headerName: t('AnelmaBovine:1012'),
            type: 'string',
            width: COL_W_S,

            renderCell: (params) => {
                return (
                    utils
                        .calculateAgeInMonths(moment(), moment(params.row.BirthDate), 30.33)
                        .toFixed(1) || ''
                );
            },
        },

        {
            field: 'MainGroup',
            headerName: t('AnelmaBovine:1102'),
            type: 'string',
            width: COL_W_M,

            valueGetter: (params, row) => {
                let maingrp = mainGroups.find((i) => i.Id === selectedMainGroup);
                if (maingrp !== undefined && maingrp?.ChildGroups.length > 0) {
                    for (let i = 0; i < maingrp?.ChildGroups.length; i++) {
                        var childgrp = maingrp.ChildGroups[i];
                        var hasAnimal = childgrp.Animals.find((a) => a.AnimalGUID === row.Id);

                        if (hasAnimal) {
                            return maingrp.Name;
                        }
                    }
                } else {
                    return mainGroups.find((m) =>
                        m.ChildGroups.find((c) => c.Animals.find((a) => a.AnimalGUID === row.Id))
                    )?.Name;
                }
                return row.value ? Number(row.value).toFixed(1) : '';
            },
        },

        {
            field: 'SubGroup',
            headerName: t('AnelmaBovine:1104'),
            type: 'string',
            width: COL_W_M,

            valueGetter: (params, row) => {
                let maingrp = mainGroups.find((i) => i.Id === selectedMainGroup);
                if (maingrp !== undefined && maingrp?.ChildGroups.length > 0) {
                    for (let i = 0; i < maingrp?.ChildGroups.length; i++) {
                        var childgrp = maingrp.ChildGroups[i];
                        var hasAnimal = childgrp.Animals.find((a) => a.AnimalGUID === row.Id);

                        if (hasAnimal) {
                            return childgrp.Name;
                        }
                    }
                } else {
                    let cg = mainGroups.find((m) =>
                        m.ChildGroups.find((c) => c.Animals.find((a) => a.AnimalGUID === row.Id))
                    )?.ChildGroups;

                    return cg?.find((c) => c.Animals.find((ca) => ca.AnimalGUID === row.Id))?.Name;
                }
                return row.value ? Number(row.value).toFixed(1) : '';
            },
        },
        {
            field: 'HoldingSiteId',
            headerName: t('AnelmaBovine:1063'),
            renderCell: (params) => {
                return (
                    <TextWithTooltip
                        text={String(params.api.getCellValue(params.id, params.field))}
                    />
                );
            },
            valueGetter: (params, row) => {
                const holdingSiteId = row.HoldingSiteId;
                return isNotNil(holdingSiteId) ? holdingSiteId : '';
            },
            width: 140,
        },

        {
            field: 'PurchaseDate',
            headerName: t('AnelmaBovine:1013'),
            type: 'string',
            width: COL_W_M,
            renderCell: (params) => <TextWithTooltip text={String(params.value)} />,
            valueGetter: (params, row) => {
                if (row.DateOfPurchase !== null && row.DateOfPurchase !== '')
                    return moment(row.DateOfPurchase).format(dayFormat);
                return '';
            },
        },
    ];

    const countAnimalAverageAge = (group: ISubGroup) => {
        let total = 0;
        let divider = 0;
        let averageAge = 0;
        let multiplier = Math.pow(10, 1 || 0);
        if (props.allAnimals != null) {
            for (let index = 0; index < group.Animals.length; index++) {
                let birthDate = props.allAnimals[0].Animals.find(
                    (i) => i.Id === group.Animals[index].AnimalGUID
                )?.BirthDate;
                if (birthDate) {
                    let age = utils.calculateAgeInMonths(moment(), moment(birthDate), 30.33);
                    total += age;
                    if (age != 0) {
                        divider += 1;
                    }
                }

                averageAge = total / divider;
            }
        }
        return Number(Math.round(averageAge * multiplier) / multiplier) || '';
    };

    const handleAnimalDataChange = (params: string[]) => {
        setAddAnimalDialogVisible(false);
        const newValues = [...selectedAnimals];
        for (let index = 0; index < params.length; index++) {
            let animal = animals.find((i) => i.Id === params[index]) as IAnimalSummary;
            if (newValues.includes(animal) === false) newValues.push(animal);
        }
        setSelectedAnimals(newValues);
    };

    const handleAnimalsToSave = (params: GridRowSelectionModel) => {
        var animalsArray = [];
        var animalsToDelete: IAnimalDeletionModel[] = [];

        for (let i = 0; i < params.length; i++) {
            let cg = mainGroups.find((m) =>
                m.ChildGroups.find((c) =>
                    c.Animals.find((a) => a.AnimalGUID === params[i].toString())
                )
            )?.ChildGroups;
            let groupId =
                cg?.find(
                    (c) => c.Animals.find((ca) => ca.AnimalGUID === params[i].toString())?.GroupId
                )?.Id || '';
            animalsArray.push(params[i].toString());
            animalsToDelete.push({ animalId: params[i].toString(), groupId: groupId });
        }

        setAnimalsToSave(animalsArray);
        setAnimalsToDelete(animalsToDelete);
    };

    const updateGroups = () => {
        getGroups(props.farmGuid);
        setAddGroupDialogVisible(false);
    };

    const deleteSubDisabled = (item: IMainGroup, child: ISubGroup) => {
        if (item.ChildGroups.length <= 1 || !auth.canDeleteGrouping) {
            return true;
        } else if (item.ChildGroups.find((i) => i.Animals.length > 0 && i.Id === child.Id)) {
            return true;
        } else {
            return false;
        }
    };

    const deleteMainDisabled = (item: IMainGroup) => {
        if (item.ChildGroups.find((i) => i.Animals.length > 0) || !auth.canDeleteGrouping) {
            return true;
        } else {
            return false;
        }
    };

    const areGroupIdsInvalid = (currentGroupId: string, formerGroupId: string) => {
        if (
            !Utils.isGuidEmpty(formerGroupId) &&
            !Utils.isGuidEmpty(currentGroupId) &&
            currentGroupId !== formerGroupId
        )
            return true;
        return false;
    };

    const getSelectedAnimalsDisabledState = () => {
        if (!createPreliminaryAnnouncements) return false;

        let animalsToVerify =  [...selectedAnimals].filter(a => dataGridAnimals.findIndex(r => r === a.Id) != -1);

        // Sex is a must
        if (animalsToVerify.some((animal) => isNil(animal.Sex) || animal.Sex === 9)) {
            enqueueSnackbar(t(`AnelmaBovine:1213`), {
                variant: 'error',
            });
            return true;
        }

        // Birth date is a must
        if (animalsToVerify.some((animal) => isNil(animal.BirthDate))) {
            enqueueSnackbar(t(`AnelmaBovine:1238`), {
                variant: 'error',
            });
            return true;
        }

        // Sex should be the same in one announcement
        if (animalsToVerify.some((animal) => animal.Sex !== animalsToVerify[0].Sex)) {
            enqueueSnackbar(t(`AnelmaBovine:1184`), {
                variant: 'error',
            });
            return true;
        }

        // holding site has to be the same
        if (animalsToVerify.some((animal) => animal.HoldingSiteId !== animalsToVerify[0].HoldingSiteId)) {
            enqueueSnackbar(t(`AnelmaBovine:1212`), {
                variant: 'error',
            });
            return true;
        }

        // all animals should be in a waiting room
        if (animalsToVerify.some((animal) => (animal.Flags & 1) === 0)) {
            enqueueSnackbar(t(`AnelmaBovine:1221`), {
                variant: 'error',
            });
            return true;
        }

        return false;
    };

    const displayPreliminaryAnnouncementSwitch = () => {
        if (
            farmSettings?.SettingType === FarmSettingTypeEnum.PreliminaryAnnouncementsInUse &&
            farmSettings?.SettingValue === 1 &&
            props.waitingRoom
        )
            return true;

        return false;
    };

    const handleCreatePreliminaryAnnouncementsValueChange = (value: boolean) => {
        setCreatePreliminaryAnnouncements(value);
    };

    const saveGroup = () => {
        if (validator.invalid) {
            enqueueSnackbar(t('AnelmaGeneral:1169'), {
                variant: 'error',
            });
            return;
        }

        if (mainGroupTarget === EmptyGUID) {
            // Me no understando
            setIsLoading(true);

            groupsApi.deleteAnimalsFromGroup(animalsToDelete).then((res) => {
                if (!res) {
                    enqueueSnackbar(t('AnelmaBovine:1177'), {
                        variant: 'error',
                    });
                    setIsLoading(false);
                    return;
                }
                getGroups(props.farmGuid);
                enqueueSnackbar(t('AnelmaBovine:1240'), {
                    variant: 'success',
                });
                setIsLoading(false);
            });
        } else {
            setIsLoading(true);
            groupsApi
                .setAnimalsInGroup(animalsToSave, subGroupTarget?.toString() || '')
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaBovine:1176'), {
                            variant: 'error',
                        });
                        setIsLoading(false);
                    }
                    enqueueSnackbar(t('AnelmaBovine:1240'), {
                        variant: 'success',
                    });

                    getGroups(props.farmGuid);
                    setIsLoading(false);
                });
        }

        if (displayPreliminaryAnnouncementSwitch() === true
            && createPreliminaryAnnouncements === true) {
            // •	Emolehmätila = SucklerCowCattleBrokerPurchase,  1 = emolehmätila     Calving
            // •	Lypsykarjatila = N/A,                           2 = lypsykarjatila   Dairy
            // •	Kasvattaja = BreederBrokerPurchase ,            3 = kasvattaja       Upbringing
            // •	Välikasvattamo = RearingUnitBrokerPurchase,     4 = välikasvattamo   Transmission

            let farmingTypes = getFarmingTypes();

            if (farmingTypes.includes(3) && farmingTypes.includes(4)) {
                // Kasvattamo ja välikasvattamo
                setShowCreateAnnouncementDialog(true);
            } else if (farmingTypes.includes(3)) {
                // Kasvattamo
                savePreliminarySlaughter();
            } else if (farmingTypes.includes(4)) {
                // Välikasvattamo
                savePreliminaryIntermediation();
            }
        }
        else {
            setSelectedAnimals([]);
        }
    };

    const savePreliminarySlaughter = async () => {
        const newAnnouncementType = AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement;
        const fullAnimalDataToSave = props.allAnimals
            ?.map((subArray) =>
                subArray.Animals.filter((animal) => animalsToSave.includes(animal.Id))
            )
            .flat()
            .filter((value, index, self) => index === self.findIndex((t) => t.Id === value.Id));

        if (farmDetails && fullAnimalDataToSave && fullAnimalDataToSave?.length > 0) {
            let announcementData: ICollectWeekData = {
                FarmId: farmDetails.Id || '',
                AnnouncementType: newAnnouncementType,
                Animals: fullAnimalDataToSave,
            };

            await savePreliminaryAnnouncement(announcementData);
        }
    }

    const savePreliminaryIntermediation = async () => {
        const newAnnouncementType = AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement;
        const fullAnimalDataToSave = props.allAnimals
            ?.map((subArray) =>
                subArray.Animals.filter((animal) => animalsToSave.includes(animal.Id))
            )
            .flat()
            .filter((value, index, self) => index === self.findIndex((t) => t.Id === value.Id));

        if (farmDetails && fullAnimalDataToSave && fullAnimalDataToSave?.length > 0) {
            let announcementData: ICollectWeekData = {
                FarmId: farmDetails.Id || '',
                AnnouncementType: newAnnouncementType,
                Animals: fullAnimalDataToSave,
            };

            await savePreliminaryAnnouncement(announcementData);
        }
    }

    const savePreliminaryAnnouncement = async (announcementData: ICollectWeekData) => {
        if (farmDetails) {
            await animalAnnouncementApi
                .getPreliminaryAnnouncementCollectWeek(announcementData)
                .then(async (response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaBovine:1241'), {
                            variant: 'error',
                        });
                        return;
                    }

                    if (!CheckWeekNotInPast(response.Entity.start)
                        && !CheckWeekNotInPast(response.Entity.end)) {
                        enqueueSnackbar(t('AnelmaBovine:1241'), {
                            variant: 'error',
                        });
                        enqueueSnackbar(t('AnelmaAnimalAnnouncement:1037'), {
                            variant: 'error',
                        });
                        return;
                    }

                    setCollectWeekStart(response.Entity.start);
                    setCollectWeekEnd(response.Entity.end);

                    const bovineInfos = mapBovineInfos(
                        mapAnimalSummaryToIAnimalAnnouncementAnimal(
                            announcementData.Animals
                        ),
                        farmDetails.HoldingSites[0].HoldingSiteId,
                        response?.Entity.start,
                        response?.Entity.end
                    );

                    bovineInfos.forEach(b =>
                        b.SalesType = defaultSalesType(
                            announcementData.AnnouncementType,
                            b.Sex
                        )
                    );

                    let payload: ICreateAnnouncement = {
                        AnnouncementInfo: '',
                        BovineInfos: bovineInfos,
                        FarmId: farmDetails.Id || '',
                        HasFiles: false,
                        ProducerNumber: producerNumber,
                    };

                    setPreliminaryAnnouncementData(payload);

                    let createPreliminaryAnnouncement = (payload: ICreateAnnouncement) => animalAnnouncementApi
                        .createPreliminaryBovineIntermediationAnnouncement(payload);

                    if (announcementData.AnnouncementType == AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) {
                        createPreliminaryAnnouncement = (payload: ICreateAnnouncement) => animalAnnouncementApi
                            .createPreliminaryBovineSlaughterAnnouncement(payload);
                    }

                    await createPreliminaryAnnouncement(payload)
                        .then((newAnnouncement) => {
                            if (!newAnnouncement) {
                                enqueueSnackbar(t('AnelmaBovine:1241'), {
                                    variant: 'error',
                                });
                                return;
                            }

                            toggleDialogs(newAnnouncement.Entity.AnnouncementId, announcementData.AnnouncementType);

                            if (farmDetails)
                                props.addNewAnnouncementToList(newAnnouncement.Entity, announcementData.AnnouncementType, farmDetails.HoldingSites[0].HoldingSiteId);
                        })
                        .catch((error) => {
                            enqueueSnackbar(t('AnelmaBovine:1241'), {
                                variant: 'error',
                            });
                        });
                })
                .catch((error) => {
                    enqueueSnackbar(t('AnelmaBovine:1241'), {
                        variant: 'error',
                    });
                });
        }
    }

    const CheckWeekNotInPast = (collectWeek: ICollectWeek) => {
        if (collectWeek.Year > moment().year())
            return true;

        return collectWeek.Year == moment().year() && collectWeek.Week >= moment().week();
    }

    const getFarmingTypes = () => {
        let farmingTypes = [];
        if (farmDetails) {
            for (let i = 0; i < farmDetails?.ProductionLines.length; i++) {
                farmingTypes.push(...farmDetails.ProductionLines[i].FarmingTypes);
            }
        }
        return farmingTypes;
    };

    const toggleDialogs = (announcementId: GUIDType, announcementType: AnnouncementTypeEnum) => {
        setAnnouncementId(announcementId);
        setAnnouncementType(announcementType);
        setShowCreateAnnouncementDialog(false);
        setShowCreatePurchaseDialog(true);
    };

    const footerActionButtonsGroups: ADialogButtonsProp = {
        left: [
            {
                onClick: () => {
                    props.onDataChange(mainGroups);
                    props.onClose();
                },

                type: 'cancel',
            },
        ],
        right: [],
    };

    const footerActionButtonsGrouping: ADialogButtonsProp = {
        left: [
            {
                onClick: () => {
                    props.onDataChange(mainGroups);
                    props.onClose();
                },
                type: 'cancel',
            },
            {
                onClick: () => setAddAnimalDialogVisible(true),
                type: 'default',
                label: t('AnelmaBovine:1049'),
                disabled: !auth.canCreateGrouping || isLoading,
            },
        ],
        right: [
            {
                onClick: () => {
                    saveGroup();
                },
                disabled:
                    !(
                        (mainGroupTarget === EmptyGUID && animalsToSave.length !== 0) ||
                        ((subGroupTarget || '').toString().length > 0 && animalsToSave.length !== 0)
                    ) ||
                    !auth.canCreateGrouping ||
                    !auth.canDeleteGrouping ||
                    !auth.canUpdateGrouping ||
                    isLoading ||
                    getSelectedAnimalsDisabledState(),
                type: 'save',
            },
        ],
    };

    const openAddDialog = () => {
        setAddGroupDialogVisible(true);
    };

    const deleteSubGroup = () => {
        if (subGroupToDelete) {
            groupsApi.deleteGroup(subGroupToDelete.Id).then(() => {
                enqueueSnackbar(t('AnelmaGeneral:1018'), {
                    variant: 'success',
                });
                updateGroups();
            });
        }
    };

    const deleteAnimalFromGroup = () => {
        if (animalToDelete !== null) {
            groupsApi.deleteAnimalsFromGroup([animalToDelete]).then((res) => {
                getGroups(props.farmGuid);
                enqueueSnackbar(t('AnelmaGeneral:1018'), {
                    variant: 'success',
                });
            });
        }
    };

    const deleteMainGroup = () => {
        if (mainGroupToDelete) {
            groupsApi.deleteGroup(mainGroupToDelete.Id).then(() => {
                enqueueSnackbar(t('AnelmaGeneral:1018'), {
                    variant: 'success',
                });
                updateGroups();
            });
        }
    };

    const toggleSubItems = (group: IMainGroup, index: number) => {
        const newValues = [...toggleTracker];
        newValues[index].toggleStatus = !newValues[index].toggleStatus;
        setToggleTracker(newValues);
    };

    const toggleChecker = (group: IMainGroup) => {
        return toggleTracker.find((i) => i.id === group.Id)?.toggleStatus;
    };

    const countAnimals = (group: IMainGroup) => {
        var animalCount = 0;
        for (let i = 0; i < group.ChildGroups.length; i++) {
            for (let index = 0; index < group.ChildGroups[i].Animals.length; index++) {
                animalCount += 1;
            }
        }
        return animalCount;
    };

    const getEUIdentifierToDelete = () => {
        if (animalToDelete === null)
            return '';

        let i = animals.findIndex(a => a.Id === animalToDelete.animalId);

        if (i != -1) {
            return animals[i].EuIdentifier;
        }

        return '';
    } 

    return (
        <>
            <ADialog open onClose={props.onClose} disableEnforceFocus>
                <ADialogTitle>{t('AnelmaBovine:1101')}</ADialogTitle>
                <ARadioButtons
                    validator={validator}
                    value={selectedDialogOption}
                    id={'dialog-selector'}
                    items={dialogOptions}
                    onChange={(e: any) => setSelectedDialogOption(e)}
                ></ARadioButtons>

                {selectedDialogOption === 2 && (
                    <Grid>
                        {showAnnotation && (
                            <Annotation Annotations={[t('AnelmaBovine:1115')]}></Annotation>
                        )}
                        <ADialogContent size={'lg'} isLoading={isLoading}>
                            {displayPreliminaryAnnouncementSwitch() && (
                                <CreatePreliminaryAnnouncementsForGroupSwitch
                                    handleCreatePreliminaryAnnouncementsValueChange={
                                        handleCreatePreliminaryAnnouncementsValueChange
                                    }
                                />
                            )}

                            {mainGroupSelections.length > 0 && (
                                <Grid container style={{ justifyContent: 'center' }}>
                                    <Grid style={{ flexGrow: 1 }}>
                                        <AAutocomplete
                                            label={t('AnelmaBovine:1102')}
                                            id='selected-main-group'
                                            onChange={(v) => {
                                                setSelectedMainGroup(v);
                                                setSubGroupSelections([]);
                                                setSelectedSubGroup('');
                                            }}
                                            items={mainGroupSelections}
                                            validator={validator}
                                            value={selectedMainGroup}
                                        />
                                    </Grid>
                                    <Grid style={{ flexGrow: 1 }}>
                                        <AAutocomplete
                                            label={t('AnelmaBovine:1105')}
                                            id='main-group-target'
                                            onChange={(v) => {
                                                setMainGroupTarget(v);
                                                setSubGroupTargetSelections([]);
                                                setSubGroupTarget('');
                                            }}
                                            items={mainGroupSelections}
                                            required
                                            validator={validator}
                                            value={mainGroupTarget}
                                            forceValidEmpty
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            <Grid container style={{ justifyContent: 'center' }}>
                                <Grid style={{ flexGrow: 1, maxWidth: '50%' }}>
                                    {subGroupSelections.length > 0 && (
                                        <AAutocomplete
                                            label={t('AnelmaBovine:1104')}
                                            id='selected-sub-group'
                                            onChange={(v) => {
                                                setSelectedSubGroup(v);
                                            }}
                                            items={subGroupSelections}
                                            validator={validator}
                                            value={selectedSubGroup}
                                        />
                                    )}
                                </Grid>
                                <Grid style={{ flexGrow: 1, maxWidth: '50%' }}>
                                    {subGroupTargetSelections.length > 0 && (
                                        <AAutocomplete
                                            label={t('AnelmaBovine:1106')}
                                            id='sub-group-target'
                                            onChange={(v) => {
                                                setSubGroupTarget(v);
                                            }}
                                            items={subGroupTargetSelections}
                                            required
                                            validator={validator}
                                            value={subGroupTarget}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                            <Grid>
                                <ADataGrid
                                    actions={[
                                        (params) => {
                                            return (
                                                selectedMainGroup !==
                                                    '00000000-0000-0000-0000-000000000000' && (
                                                    <Grid>
                                                        <ADeleteIconButton
                                                            key={`${params.id}-delete`}
                                                            onClick={() => {
                                                                var animalToDelete: IAnimalDeletionModel[] =
                                                                    [];
                                                                let childGroupId = '';

                                                                let cg = mainGroups.find((m) =>
                                                                    m.ChildGroups.find((c) =>
                                                                        c.Animals.find(
                                                                            (a) =>
                                                                                a.AnimalGUID ===
                                                                                params.row.Id
                                                                        )
                                                                    )
                                                                )?.ChildGroups;

                                                                childGroupId =
                                                                    cg?.find(
                                                                        (c) =>
                                                                            c.Animals.find(
                                                                                (ca) =>
                                                                                    ca.AnimalGUID ===
                                                                                    params.row.Id
                                                                            )?.AnimalGUID
                                                                    )?.Id || '';

                                                                setAnimalToDelete({
                                                                    animalId: params.row.Id,
                                                                    groupId: childGroupId,
                                                                });
                                                            }}
                                                        />
                                                    </Grid>
                                                )
                                            );
                                        },
                                    ]}
                                    columns={columns}
                                    rows={
                                        selectedAnimals.length > 0
                                            ? selectedAnimals
                                                // filter duplicates
                                                .filter((a, i) => selectedAnimals.findIndex(sameId => sameId.Id === a.Id) === i)
                                                // add id as a row key
                                                .map((i) => mergeRight(i, { id: i.Id }))
                                            : []
                                    }
                                    enableFiltering={true}
                                    onSelectionModelChange={(selectionModel) => {
                                        handleAnimalsToSave(selectionModel);
                                        setDataGridAnimals(selectionModel);
                                    }}
                                    selectionModel={dataGridAnimals}
                                    enablePagination
                                ></ADataGrid>
                            </Grid>
                        </ADialogContent>
                        <Grid className='animal-announcement-grid-floating-bottom'>
                            <ADialogActions
                                buttons={footerActionButtonsGrouping}
                                hasBorder={false}
                            />
                        </Grid>
                    </Grid>
                )}

                {showConfirmDeleteAnimal && 
                    <ConfirmDeletionDialog
                        open={showConfirmDeleteAnimal}
                        onClose={() => {
                            setAnimalToDelete(null);
                            setShowConfirmDeleteAnimal(false);
                        }}
                        onDelete={() => deleteAnimalFromGroup()}
                        confirmation={t(
                            'AnelmaBovine:1117'
                        ).replace(
                            '{EUIdentifier}',
                            getEUIdentifierToDelete()
                        )}
                    ></ConfirmDeletionDialog>
                }

                {selectedDialogOption === 1 && (
                    <Grid>
                        <ADialogContent size={'lg'} isLoading={isLoading}>
                            <Table style={{ maxWidth: '80%', margin: '10px 40px' }}>
                                <TableRow>
                                    <TableCell style={{ minWidth: '50px' }}>#</TableCell>

                                    <TableCell style={{ minWidth: '50px' }}></TableCell>
                                    <TableCell style={{ minWidth: '80px' }}>
                                        {t('AnelmaBovine:1102')}
                                    </TableCell>
                                    <TableCell style={{ minWidth: '80px' }}>
                                        {t('AnelmaBovine:1110')}
                                    </TableCell>
                                    <TableCell>{t('AnelmaBovine:1037')}</TableCell>
                                </TableRow>
                                {mainGroups.map((item, index) => {
                                    return (
                                        <TableBody>
                                            <TableRow>
                                                <TableCell style={{ maxWidth: '10px' }}>
                                                    {toggleChecker(item) === false && (
                                                        <ExpandMore
                                                            onClick={() =>
                                                                toggleSubItems(item, index)
                                                            }
                                                        />
                                                    )}
                                                    {toggleChecker(item) === true && (
                                                        <ExpandLess
                                                            onClick={() =>
                                                                toggleSubItems(item, index)
                                                            }
                                                        />
                                                    )}
                                                </TableCell>

                                                <TableCell style={{ minWidth: '15px' }}>
                                                    <ADeleteIconButton
                                                        style={{ marginRight: '20px' }}
                                                        onClick={() => {
                                                            setMainGroupToDelete(item);
                                                            setShowConfirmDeleteMain(true);
                                                        }}
                                                        disabled={deleteMainDisabled(item)}
                                                    />
                                                    <AEditIconButton
                                                        onClick={() => {
                                                            setAddDialogData(item);
                                                            setAddDialogMode('edit');
                                                            openAddDialog();
                                                        }}
                                                        disabled={!auth.canUpdateGrouping}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ minWidth: '50px' }}>
                                                    {item.Name}
                                                </TableCell>
                                                <TableCell style={{ minWidth: '50px' }}>
                                                    {item.ChildGroups.length}
                                                </TableCell>
                                                <TableCell style={{ minWidth: '50px' }}>
                                                    {countAnimals(item)}
                                                </TableCell>

                                                <ConfirmDeletionDialog
                                                    open={showConfirmDeleteMain}
                                                    onClose={() => setShowConfirmDeleteMain(false)}
                                                    onDelete={() => deleteMainGroup()}
                                                    confirmation={t('AnelmaBovine:1114')}
                                                ></ConfirmDeletionDialog>
                                            </TableRow>
                                            {toggleChecker(item) && (
                                                <TableRow>
                                                    <TableCell
                                                        style={{ minWidth: '50px' }}
                                                    ></TableCell>

                                                    <TableCell style={{ minWidth: '80px' }}>
                                                        {t('AnelmaBovine:1104')}
                                                    </TableCell>

                                                    <TableCell>{t('AnelmaBovine:1037')}</TableCell>
                                                    <TableCell>{t('AnelmaBovine:1166')}</TableCell>
                                                </TableRow>
                                            )}
                                            {toggleChecker(item) &&
                                                item.ChildGroups.map((child, ind) => {
                                                    return (
                                                        <TableRow>
                                                            <TableCell style={{ minWidth: '15px' }}>
                                                                <ADeleteIconButton
                                                                    style={{ marginRight: '20px' }}
                                                                    disabled={deleteSubDisabled(
                                                                        item,
                                                                        child
                                                                    )}
                                                                    onClick={() => {
                                                                        setSubGroupToDelete(child);
                                                                        setShowConfirmDeleteSub(
                                                                            true
                                                                        );
                                                                    }}
                                                                />
                                                            </TableCell>

                                                            <TableCell style={{ minWidth: '50px' }}>
                                                                {child.Name}
                                                            </TableCell>
                                                            <TableCell style={{ minWidth: '50px' }}>
                                                                {child.Animals.length}
                                                            </TableCell>
                                                            <TableCell style={{ minWidth: '50px' }}>
                                                                {countAnimalAverageAge(child)}
                                                            </TableCell>
                                                            <ConfirmDeletionDialog
                                                                open={showConfirmDeleteSub}
                                                                onClose={() =>
                                                                    setShowConfirmDeleteSub(false)
                                                                }
                                                                onDelete={() => deleteSubGroup()}
                                                                confirmation={t(
                                                                    'AnelmaBovine:1114'
                                                                )}
                                                            ></ConfirmDeletionDialog>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    );
                                })}
                            </Table>

                            <Grid
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    paddingRight: '20px',
                                }}
                            >
                                <AAddButton
                                    onClick={() => {
                                        setAddDialogMode('add');
                                        openAddDialog();
                                    }}
                                    type='action'
                                    disabled={!auth.canCreateGrouping || isLoading}
                                >
                                    {t('AnelmaBovine:1111')}
                                </AAddButton>
                            </Grid>
                        </ADialogContent>
                        <ADialogActions buttons={footerActionButtonsGroups} />
                    </Grid>
                )}

                {addAnimalDialogVisible && (
                    <AddAnimalDialog
                        onClose={() => setAddAnimalDialogVisible(false)}
                        animals={animals}
                        fromDialog={SecondaryDialogTypeEnum.AddAnimal}
                        handleAnimalDataChange={handleAnimalDataChange}
                    />
                )}
                {showCreateAnnouncementDialog && (
                    <CreatePreliminaryAnnouncementDialog
                        onClose={() => setShowCreateAnnouncementDialog(false)}
                        onSave={async (newAnnouncementType: AnnouncementTypeEnum) => {
                            if (newAnnouncementType === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) {
                                await savePreliminarySlaughter();
                            }
                            else if (newAnnouncementType === AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) {
                                await savePreliminaryIntermediation();
                            }
                        }}
                    ></CreatePreliminaryAnnouncementDialog>
                )}
                {showCreatePurchaseDialog &&
                    preliminaryAnnouncementData &&
                    announcementId &&
                    collectWeekStart &&
                    collectWeekEnd &&
                    announcementType && (
                        <CreatePreliminaryPurchaseAnnouncementDialog
                            preliminaryAnnouncementData={preliminaryAnnouncementData}
                            onClose={(newAnnouncement?: IUpdateAnnouncementResult) => {
                                if (newAnnouncement && farmDetails)
                                    props.addNewAnnouncementToList(
                                        newAnnouncement,
                                        AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement,
                                        farmDetails.HoldingSites[0].HoldingSiteId);

                                setSelectedAnimals([]);
                                setShowCreatePurchaseDialog(false);

                                if (newAnnouncement !== undefined)
                                    setShowCreatedPreliminaryAnnouncementsDialog(true);
                                else
                                    props.onClose();
                            }}
                            linkedAnnouncementId={announcementId}
                            linkedAnnouncementType={announcementType}
                            collectWeekStart={getPurchaceCollectWeek(collectWeekStart)}
                            collectWeekEnd={getPurchaceCollectWeek(collectWeekEnd)}
                        />
                    )}
                {showCreatedPreliminaryAnnouncementsDialog &&
                    preliminaryAnnouncementData &&
                    announcementId &&
                    announcementType &&
                    collectWeekStart &&
                    collectWeekEnd && (
                        <CreatedPreliminaryAnnouncementsDialog
                            onClose={() => {
                                setShowCreatedPreliminaryAnnouncementsDialog(false);
                                props.onClose();
                            }}
                            mainAnnouncementType={announcementType}
                            mainAnnouncementCollectWeekStart={collectWeekStart}
                            mainAnnouncementCollectWeekEnd={collectWeekEnd}
                            linkedPurchaseAnnouncementCollectWeekStart={getPurchaceCollectWeek(collectWeekStart)}
                            linkedPurchaseAnnouncementCollectWeekEnd={getPurchaceCollectWeek(collectWeekEnd)}
                        />
                    )}
                {addGroupDialogVisible && (
                    <AddGroupDialog
                        onClose={() => updateGroups()}
                        mode={addDialogMode}
                        data={addDialogData}
                        existingGroups={mainGroups}
                    />
                )}
            </ADialog>
        </>
    );
}
